import React, { useState, useCallback } from 'react';
import pLimit from 'p-limit';

import {
	useInitiateMultipartFileUploadMutation,
	useUploadMultipleChunkMutation,
	useCompleateMultipleChunkUploadMutation
} from '@/api/S3UploadApis';
import CommonImage from '@/components/CommonImage';
import ModalContainer from '@/components/modals/ModalContainer';
import { toastError, toastSuccess, toastWarning } from '@/components/Toast';

const MAX_PSD_FILES = 100;
const MAX_IMAGE_FILES = 200;
const MAX_CONCURRENT_UPLOADS = 3;
const MAX_RETRIES = 3;

const UPLOAD_ENDPOINTS = {
	IMAGE_INITIATE: (folderId) => `/product/upload-bulk-presentation/${folderId}/initiate`,
	PSD_INITIATE: (folderId) => `/archived-doc/upload-bulk-presentation-file/${folderId}/initiate`,
	IMAGE_COMPLETE: '/product/upload-bulk-presentation/complete',
	PSD_COMPLETE: '/archived-doc/upload-bulk-presentation-file/complete'
};

const BulkPresentationUploadModal = ({
	onClose,
	setIsUploadedPresentation,
	setUploadedProductIds,
	folderId
}) => {
	const [isImageUploading, setIsImageUploading] = useState(false);
	const [isPsdUploading, setIsPsdUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isViewPresentation, setIsViewPresentation] = useState(false);
	const [imageResponse, setImageResponse] = useState([]);
	const [psdResponse, setPsdResponse] = useState([]);
	const [isPsdUploadCompleted, setIsPsdUploadCompleted] = useState(false);
	const [isImageUploadCompleted, setIsImageUploadCompleted] = useState(false);

	const [initiateMultipartFileUpload] = useInitiateMultipartFileUploadMutation();
	const [compleateMultipleChunkUpload] = useCompleateMultipleChunkUploadMutation();
	const [uploadMultipleChunk] = useUploadMultipleChunkMutation();

	// note*  Create 1MB chunk from first file for warm-up the upload with 100ms delay
	const createWarmUpChunk = useCallback((firstFile) => {
		const chunkSize = 1024 * 1024; // 1MB
		const fileSlice = firstFile.slice(0, chunkSize);
		return fileSlice;
	}, []);

	const warmUpUpload = useCallback(
		async (firstFile, firstSignedUrl) => {
			try {
				const warmUpChunk = createWarmUpChunk(firstFile);

				const headers = {
					'x-amz-acl': 'public-read'
				};

				if (firstFile.name.toLowerCase().endsWith('.psd')) {
					headers['Content-Type'] = 'image/vnd.adobe.photoshop';
				} else {
					headers['Content-Type'] = firstFile.type;
				}

				await uploadMultipleChunk({
					url: firstSignedUrl,
					body: warmUpChunk,
					headers
				});

				await new Promise((resolve) => setTimeout(resolve, 100));
			} catch {}
		},
		[uploadMultipleChunk, createWarmUpChunk]
	);

	const validateFileCounts = useCallback((files, fileType) => {
		const maxFiles = fileType === 'PSD' ? MAX_PSD_FILES : MAX_IMAGE_FILES;
		if (files.length > maxFiles) {
			toastError(`Maximum ${maxFiles} ${fileType} files allowed`);
			return false;
		}
		return true;
	}, []);

	const uploadWithRetry = useCallback(
		async (file, url, retryCount = 0) => {
			try {
				const headers = {
					'x-amz-acl': 'public-read'
				};

				if (file.name.toLowerCase().endsWith('.psd')) {
					headers['Content-Type'] = 'image/vnd.adobe.photoshop';
				} else {
					headers['Content-Type'] = file.type;
				}

				const response = await uploadMultipleChunk({
					url,
					body: file,
					headers
				});

				if (response?.error) {
					throw new Error('Failed to upload file');
				}

				return {
					partNumber: 1,
					eTag: response.data?.eTag?.replaceAll('"', '') || ''
				};
			} catch {
				if (retryCount < MAX_RETRIES) {
					await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));
					return uploadWithRetry(file, url, retryCount + 1);
				}
				toastError(`Failed to upload file ${file.name} after ${MAX_RETRIES} retries`);
				return null;
			}
		},
		[uploadMultipleChunk]
	);

	const uploadFilesInBatches = useCallback(
		async (files, urlsResponse = []) => {
			const results = [];
			const totalFiles = files.length;
			let completedFiles = 0;

			const limit = pLimit(MAX_CONCURRENT_UPLOADS);

			const processUpload = async (file, urlData) => {
				if (!urlData) {
					completedFiles++;
					setUploadProgress((completedFiles / totalFiles) * 100);
					return null;
				}

				const result = await uploadWithRetry(file, urlData.urlList[0]);

				completedFiles++;
				setUploadProgress((completedFiles / totalFiles) * 100);

				if (!result) {
					return null;
				}

				return {
					...result,
					documentId: urlData.documentId,
					objectKey: urlData.objectKey,
					productId: urlData.productId,
					partList: [{ partNumber: 1, eTag: result.eTag }]
				};
			};

			const uploadPromises = files.map((file, index) =>
				limit(() => processUpload(file, urlsResponse[index]))
			);

			const uploadResults = await Promise.all(uploadPromises);

			uploadResults.forEach((result) => {
				if (result) {
					results.push(result);
				}
			});

			return results;
		},
		[uploadWithRetry]
	);

	const createRequestBody = useCallback((filesToUpload) => {
		return filesToUpload?.map((file) => ({
			name: file.name,
			docMimeType:
				file.type || (file.name.toLowerCase().endsWith('.psd') ? 'image/vnd.adobe.photoshop' : ''),
			documentType: 'PRODUCT_DESIGN'
		}));
	}, []);

	const getSuccessMessage = useCallback((uploadResults, filesToUpload, fileType) => {
		return uploadResults.length === filesToUpload.length
			? `All ${fileType} files uploaded successfully!`
			: `${uploadResults.length} out of ${filesToUpload.length} ${fileType} files uploaded successfully.`;
	}, []);

	const handleUpload = useCallback(
		async (filesToUpload, fileType) => {
			const endpoint =
				fileType === 'PSD'
					? UPLOAD_ENDPOINTS.PSD_INITIATE(folderId)
					: UPLOAD_ENDPOINTS.IMAGE_INITIATE(folderId);

			if (!validateFileCounts(filesToUpload, fileType)) return;

			if (fileType === 'PSD') {
				setIsPsdUploading(true);
				setIsPsdUploadCompleted(false);
			} else {
				setIsImageUploading(true);
				setIsImageUploadCompleted(false);
			}
			setUploadProgress(0);

			try {
				const requestBody = createRequestBody(filesToUpload, fileType);
				// Initiate file upload
				const initResponse = await initiateMultipartFileUpload({
					body: requestBody,
					url: endpoint
				});

				if (fileType === 'PSD') {
					setPsdResponse(initResponse?.data);
				} else {
					setImageResponse(initResponse?.data);
				}

				if (!initResponse?.data) {
					toastError('Failed to get upload URLs');
					return;
				}

				// make a worm up call with 100ms delay to url[0] with 1mb chunk (*note: It's a dummy chunk)
				if (fileType === 'PSD' && filesToUpload.length > 0 && initResponse.data[0]?.urlList[0]) {
					await warmUpUpload(filesToUpload[0], initResponse.data[0].urlList[0]);
				}

				// Upload File in batches (3 at a time)
				const uploadResults = await uploadFilesInBatches(filesToUpload, initResponse.data);

				if (uploadResults.length > 0) {
					// Only complete uploads for successfully uploaded files
					const completeRequestBody = uploadResults.map((result) => ({
						documentId: result.documentId,
						productId: result.productId
					}));

					// Complete file upload
					const completeResponse = await compleateMultipleChunkUpload({
						body: completeRequestBody,
						url:
							fileType === 'PSD'
								? UPLOAD_ENDPOINTS.PSD_COMPLETE
								: UPLOAD_ENDPOINTS.IMAGE_COMPLETE
					});

					if (completeResponse?.data?.success) {
						const successMessage = getSuccessMessage(uploadResults, filesToUpload, fileType);
						toastSuccess(successMessage);
						setIsViewPresentation(true);
						if (fileType === 'PSD') {
							setIsPsdUploadCompleted(true);
						} else {
							setIsImageUploadCompleted(true);
						}

						if (uploadResults.length < filesToUpload.length) {
							const failedCount = filesToUpload.length - uploadResults.length;
							toastWarning(`${failedCount} files failed to upload`);
						}
					} else {
						toastError(completeResponse?.data?.message || 'Failed to complete upload');
					}
				} else {
					toastError('All files failed to upload');
				}
			} catch {
				toastError('Upload failed');
			} finally {
				setIsImageUploading(false);
				setIsPsdUploading(false);
				setUploadProgress(0);
			}
		},
		[
			folderId,
			validateFileCounts,
			createRequestBody,
			uploadFilesInBatches,
			getSuccessMessage,
			initiateMultipartFileUpload,
			compleateMultipleChunkUpload,
			warmUpUpload
		]
	);

	const handleFileChange = useCallback(
		(e, fileType) => {
			const files = Array.from(e.target.files);
			handleUpload(files, fileType);
			e.target.value = null;
		},
		[handleUpload]
	);

	const handleViewPresentation = () => {
		setIsUploadedPresentation(true);
		const uploadedProductIds =
			imageResponse?.map((item) => item?.productId) ||
			psdResponse?.map((item) => item?.productId) ||
			[];
		const uniqueUploadedProductIds = [...new Set(uploadedProductIds)];
		setUploadedProductIds(uniqueUploadedProductIds);
	};

	const isUploading = useCallback(() => {
		return isImageUploading || isPsdUploading;
	}, [isImageUploading, isPsdUploading]);

	return (
		<ModalContainer onClose={onClose} className="presentation-upload-modal">
			<div className="common-popup presentation-upload-modal">
				<div className="common-popup-header d-flex justify-content-between">
					<div className="popup-tilte">
						<h3 className="semibold-16 mb-0">Bulk Upload Presentation</h3>
					</div>
					<div className="close-btn" onClick={() => !isUploading() && onClose()}>
						<CommonImage src="/icons/close.svg" alt="close" />
					</div>
				</div>
				<div className="common-popup-body py-8">
					<div className="d-flex flex-column">
						{isUploading() && (
							<div className="progress-info task-progress-info  mb-2 mt-0 d-flex align-items-center gap-2 w-75">
								<div className="progress progress-square w-100">
									<div
										className="progress-bar"
										style={{ width: `${uploadProgress}%`, transition: 'width 0.3s ease' }}
									/>
								</div>
								<span className="progress-text">{Math.round(uploadProgress)}%</span>
							</div>
						)}
						<div className="d-flex inline-upload mb-4 w-75">
							<label
								htmlFor="image-upload"
								className={`${isUploading() || isImageUploadCompleted ? 'disabled cursor-none' : ''}`}
							>
								<CommonImage src="/icons/upload-dark.svg" alt="upload" />
								{isImageUploading ? 'Uploading...' : 'Image Upload'}
							</label>
							<input
								type="file"
								id="image-upload"
								accept="image/*"
								multiple
								className="d-none"
								onChange={(e) => handleFileChange(e, 'IMAGE')}
								disabled={isUploading() || isImageUploadCompleted}
							/>
							<label
								htmlFor="psd-upload"
								className={`ml-2 cursor-pointer ${isUploading() || isPsdUploadCompleted ? 'disabled cursor-none' : ''}`}
							>
								<CommonImage src="/icons/upload-dark.svg" alt="upload" />
								{isPsdUploading ? 'Uploading...' : 'Upload PSD File'}
							</label>
							<input
								type="file"
								id="psd-upload"
								accept=".psd"
								multiple
								className="d-none"
								onChange={(e) => handleFileChange(e, 'PSD')}
								disabled={isUploading() || isPsdUploadCompleted}
							/>
						</div>

						{isViewPresentation && isImageUploadCompleted && (
							<div className="text-right">
								<button className="button size32 mt-4" onClick={handleViewPresentation}>
									View Presentation
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default BulkPresentationUploadModal;
